import { DiwaSigil } from '@/components/DiwaSigil'
import { Fragment, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { AppearancePanel, Button, ThemeToggle } from '@diwata/ironvale'
import { useDomainSelection } from '@/store/domainSelection'
import { useSelection } from '@/store/selection'
import { GraphEditor } from '@/components/GraphEditor'
import { Familiar } from '@/components/Familiar'
import { EntityPeekProvider } from '@/components/EntityPeek'
import { DomainCreate, DomainInspect } from '@/components/DomainPanel'
import { MissionQueue } from '@/components/MissionQueue'
import { Dossier } from '@/components/Dossier'
import { GraphViewTab } from '@/components/GraphViewTab'
import { NeedsYou, useNeedsYou } from '@/components/NeedsYou'
import { useSpringSelection } from '@/store/springSelection'
import { useLedgerSelection } from '@/store/ledgerSelection'
import { DiwaAPI } from '@/api/client'
import { useUI } from '@/store/ui'
import { Feedback } from '@/components/Feedback'
import { HomeContext } from '@/components/ActivityHeatmap'
import { ShowMe } from '@/components/ShowMe'

function navClass({ isActive }: { isActive: boolean }) {
  return isActive ? 'iv-nav-item iv-nav-item--active' : 'iv-nav-item'
}

// ── control-plane tab sets per page (✦ agent is appended to every page) ──
type PageKey = 'constellation' | 'domains' | 'missions' | 'springs' | 'ledger' | 'other'
function pageOf(pathname: string): PageKey {
  if (pathname.startsWith('/graph')) return 'constellation'
  if (pathname.startsWith('/domains')) return 'domains'
  if (pathname.startsWith('/missions')) return 'missions'
  if (pathname.startsWith('/pipelines')) return 'springs'
  if (pathname.startsWith('/ledger')) return 'ledger'
  return 'other'
}
const PAGE_TABS: Record<PageKey, { key: string; label: string }[]> = {
  constellation: [
    { key: 'dossier', label: 'dossier' },
    { key: 'view', label: 'view' },
    { key: 'add', label: 'add' },
  ],
  domains: [
    { key: 'domain', label: 'domain' },
    { key: 'create', label: 'create' },
  ],
  missions: [{ key: 'queue', label: 'queue' }],
  springs: [{ key: 'spring', label: 'spring' }],
  ledger: [{ key: 'row', label: 'row' }],
  other: [{ key: 'context', label: 'context' }],
}

/** One mark, one concept — hover tells you which is which. */
const TAB_HINTS: Record<string, string> = {
  'needs-you': 'Needs you — suggestions and gates awaiting your judgment',
  agent: '✦ Tala — ask your world anything',
  dossier: 'Dossier — the selected star, in full',
  view: 'View — focus and filters for the constellation',
  add: 'Add — place a new star or relation by hand',
  domain: 'Inspect the selected domain',
  create: 'Create a domain — see what you get before you commit',
  queue: 'Missions — the queue and its history',
  spring: 'The selected spring — schedule, health, provenance',
  row: 'The selected lake row — track it or open it as a mission',
  context: 'Context for this page — on Home, your recent activity',
}


function RowTab() {
  const sel = useLedgerSelection((st) => st.selected)
  const { diwaUrl } = useUI()
  const qc = useQueryClient()
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle')
  const [briefState, setBriefState] = useState<'idle' | 'busy' | 'error'>('idle')
  const [briefRec, setBriefRec] = useState<string | null>(null)
  useEffect(() => { setState('idle'); setBriefState('idle'); setBriefRec(null) }, [sel])
  if (!sel) {
    return (
      <div>
        <div className="dw-rail-section-title">Row</div>
        <div className="dw-rail-empty">Select a row — its record assembles here.</div>
      </div>
    )
  }
  const r = sel.row
  const name = String(r['recipient_name'] ?? r['title'] ?? r['solicitation_number'] ?? 'row')
  const fields = Object.entries(r).filter(([k, v]) => v != null && v !== '' && k !== 'id')
  async function track() {
    setState('busy')
    try {
      const api = new DiwaAPI(diwaUrl)
      await api.post('/ledger/track', { domain: sel!.domain, dataset: sel!.dataset, row: sel!.row })
      setState('done')
    } catch {
      setState('error')
    }
  }
  return (
    <div>
      <div className="dw-rail-section-title">{name}</div>
      <div className="dw-field-grid">
        {fields.slice(0, 14).map(([k, v]) => (
          <Fragment key={k}>
            <div className="dw-field-label">{k.replace(/_/g, ' ')}</div>
            <div className="dw-field-value" style={{ wordBreak: 'break-word' }}>{String(v)}</div>
          </Fragment>
        ))}
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button size="sm" variant="primary" disabled={state === 'busy' || state === 'done'} onClick={track}>
          {state === 'busy' ? 'Tracking…' : state === 'done' ? 'Tracked ✦' : '✦ Track as star'}
        </Button>
        {state === 'error' && <span className="dw-error">Couldn't track — retry.</span>}
      </div>
      {sel.dataset === 'opportunities' && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="sm" variant="ghost" disabled={briefState === 'busy' || briefRec != null}
                  onClick={async () => {
                    setBriefState('busy')
                    try {
                      const api = new DiwaAPI(diwaUrl)
                      const res = await api.post<{ brief: { recommendation: string } }>('/missions/bid-no-bid', { row: sel!.row })
                      await qc.invalidateQueries({ queryKey: ['missions'] })
                      setBriefRec(res.brief.recommendation)
                      setBriefState('idle')
                    } catch {
                      setBriefState('error')
                    }
                  }}>
            {briefState === 'busy' ? 'Weighing…' : briefRec ? `Mission created — ${briefRec}` : 'Open as a mission'}
          </Button>
          {briefRec && <Link className="dw-link" to="/missions">open Missions →</Link>}
          {briefState === 'error' && <span className="dw-error">Couldn't create — retry.</span>}
        </div>
      )}
      {String(r['source_url'] ?? '') && (
        <div style={{ marginTop: 10 }}>
          <a className="dw-link" href={String(r['source_url'])} target="_blank" rel="noreferrer">
            Open the solicitation ↗
          </a>
        </div>
      )}
      {String(r['artifact_id'] ?? '') && (
        <div className="dw-inbox-detail" style={{ marginTop: 10, wordBreak: 'break-all' }}>
          <button
            type="button"
            className="dw-link"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            onClick={async () => {
              // open synchronously inside the user gesture — an await first gets popup-blocked
              const tab = window.open('', '_blank')
              try {
                const api = new DiwaAPI(diwaUrl)
                const bronze = await api.get<unknown>(`/ledger/artifact/${String(r['artifact_id'])}`)
                const blob = new Blob([JSON.stringify(bronze, null, 2)], { type: 'application/json' })
                const url = URL.createObjectURL(blob)
                if (tab) tab.location = url
                else window.location.href = url
                setTimeout(() => URL.revokeObjectURL(url), 60_000)
              } catch {
                tab?.close()
                setState('error')
              }
            }}
          >
            Open the bronze page — every silver row points home ↗
          </button>
        </div>
      )}
    </div>
  )
}

function SpringTab() {
  const spring = useSpringSelection((st) => st.selected)
  if (!spring) {
    return (
      <div>
        <div className="dw-rail-section-title">Spring</div>
        <div className="dw-rail-empty">Select a spring — its story assembles here.</div>
      </div>
    )
  }
  const h = spring.health
  return (
    <div>
      <div className="dw-rail-section-title">{spring.display}</div>
      <div className="dw-field-grid">
        <div className="dw-field-label">Status</div>
        <div className="dw-field-value">{h?.status ?? 'never ran'}</div>
        <div className="dw-field-label">Cadence</div>
        <div className="dw-field-value">{spring.scheduleHuman}</div>
        {h?.last_run && (<><div className="dw-field-label">Last run</div>
          <div className="dw-field-value">{new Date(h.last_run).toLocaleString()}</div></>)}
        <div className="dw-field-label">Kind</div>
        <div className="dw-field-value">{spring.source_type ?? 'source'}{spring.critical ? ' · critical' : ''}</div>
        {spring.owner_product && (<><div className="dw-field-label">Owner</div>
          <div className="dw-field-value">{spring.owner_product}</div></>)}
        <div className="dw-field-label">Registry id</div>
        <div className="dw-field-value" style={{ wordBreak: 'break-all' }}>{spring.name}</div>
      </div>
      {h?.detail && (
        <div className="dw-inbox-detail" style={{ marginTop: 8 }}>{h.detail}</div>
      )}
      {spring.url && (
        <a
          className="dw-link"
          href={spring.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-block', marginTop: 8, wordBreak: 'break-all' }}
        >
          {spring.url} ↗
        </a>
      )}
      {(spring.produces?.entities?.length ?? 0) > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="dw-field-label">Fills</div>
          <div className="dw-field-value">{spring.produces!.entities.join(' · ')}</div>
          {(spring.produces!.relations?.length ?? 0) > 0 && (
            <div className="dw-inbox-detail" style={{ marginTop: 2 }}>
              draws {spring.produces!.relations.join(' · ')}
            </div>
          )}
        </div>
      )}
      <div style={{ marginTop: 14, display: 'grid', gap: 6 }}>
        <div className="dw-inbox-detail">
          Every row keeps the original page it came from — a fact always traces back to its source.
        </div>
        <Link className="dw-link" to="/ledger">view its rows in the Ledger →</Link>
        <div className="dw-inbox-detail">Run-now: coming with the gated source flow.</div>
      </div>
    </div>
  )
}

function ContextTab() {
  const { pathname } = useLocation()
  // Home gets a real context: your activity, drawn from the graph_events log.
  if (pathname.startsWith('/home')) return <HomeContext />
  return (
    <div>
      <div className="dw-rail-section-title">Context</div>
      <div className="dw-rail-empty">
        The control plane adapts to each page. This page's tools arrive as the app grows — ✦ Tala is always here.
      </div>
    </div>
  )
}

function ControlPlane({ open }: { open: boolean }) {
  const { pathname } = useLocation()
  const page = pageOf(pathname)
  const domainSel = useDomainSelection((s) => s.selected)
  const { count: needsYouCount } = useNeedsYou()
  const [active, setActive] = useState<string>('inspect')

  // clicking a domain card jumps the panel to the domain tab
  useEffect(() => {
    if (domainSel && page === 'domains') setActive('domain')
  }, [domainSel?.id, page])

  // Per-domain accent tint: when a domain is active, paint the accent axis with
  // its color; clear it otherwise. The Domain model carries no color yet, so the
  // tint stays off until it does.
  // TODO T02: applyAccent(domainColor) once domains carry a color; clearAccent() when none.



  // clicking a star jumps the panel to its dossier
  const nodeSel = useSelection((s) => s.selected)
  useEffect(() => {
    if (nodeSel && page === 'constellation') setActive('dossier')
  }, [nodeSel?.id, page])

  // clicking a spring jumps the panel to its detail
  const springSel = useSpringSelection((st) => st.selected)
  useEffect(() => {
    if (springSel && page === 'springs') setActive('spring')
  }, [springSel?.id, page])

  // clicking a ledger row jumps the panel to the record
  const rowSel = useLedgerSelection((st) => st.selected)
  useEffect(() => {
    if (rowSel && page === 'ledger') setActive('row')
  }, [rowSel, page])

  // the one queue rides every page, next to the ✦ familiar
  const tabs = [
    ...PAGE_TABS[page],
    { key: 'needs-you', label: needsYouCount > 0 ? `◈ ${needsYouCount}` : '◈' },
    { key: 'agent', label: '✦' },
  ]
  const activeKey = tabs.some((t) => t.key === active) ? active : PAGE_TABS[page][0].key

  return (
    <aside className={`dw-rail dw-panel${open ? ' dw-rail--open' : ''}`}>
      <div className="dw-rail-title">Control Plane</div>
      <div className="dw-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={[
              'dw-tab',
              activeKey === t.key ? 'dw-tab--active' : '',
              t.key === 'needs-you' && needsYouCount > 0 ? 'dw-tab--alert' : '',
            ].filter(Boolean).join(' ')}
            onClick={() => setActive(t.key)}
            title={TAB_HINTS[t.key] ?? t.label}
          >
            {t.label}
          </button>
        ))}
      </div>
      {activeKey === 'dossier' && <Dossier />}
      {activeKey === 'view' && <GraphViewTab />}
      {activeKey === 'add' && <GraphEditor />}
      {activeKey === 'domain' && <DomainInspect />}
      {activeKey === 'create' && <DomainCreate />}
      {activeKey === 'queue' && <MissionQueue />}
      {activeKey === 'spring' && <SpringTab />}
      {activeKey === 'row' && <RowTab />}
      {activeKey === 'needs-you' && <NeedsYou />}
      {activeKey === 'context' && <ContextTab />}
      {activeKey === 'agent' && <Familiar />}
    </aside>
  )
}

export function Layout() {
  const { pathname } = useLocation()
  const [navOpen, setNavOpen] = useState(false)
  const [railOpen, setRailOpen] = useState(false)
  // Bottom "More" group: hover reveals, a click pins it open.
  const [moreHover, setMoreHover] = useState(false)
  const [morePinned, setMorePinned] = useState(false)
  const moreOpen = moreHover || morePinned
  // Desktop: both panels fold away so the constellation can take the whole window.
  // Remembered per device — a preference, not a session whim.
  const [navFolded, setNavFolded] = useState(() => localStorage.getItem('dw.nav.folded') === '1')
  const [railFolded, setRailFolded] = useState(() => localStorage.getItem('dw.rail.folded') === '1')
  const foldNav = (v: boolean) => {
    setNavFolded(v)
    localStorage.setItem('dw.nav.folded', v ? '1' : '0')
  }
  const foldRail = (v: boolean) => {
    setRailFolded(v)
    localStorage.setItem('dw.rail.folded', v ? '1' : '0')
  }
  const { count: needsYouCount } = useNeedsYou()

  // navigating closes any open overlay
  useEffect(() => {
    setNavOpen(false)
    setRailOpen(false)
  }, [pathname])

  // Escape closes overlays
  useEffect(() => {
    if (!navOpen && !railOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNavOpen(false)
        setRailOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navOpen, railOpen])

  const shellClass = [
    'dw-shell',
    navFolded ? 'dw-shell--nav-folded' : '',
    railFolded ? 'dw-shell--rail-folded' : '',
  ].filter(Boolean).join(' ')

  return (
    <EntityPeekProvider>
    <div className={shellClass}>
      <header className="dw-appbar dw-panel">
        <button className="dw-appbar-btn" aria-label="Open navigation" onClick={() => setNavOpen(true)}>☰</button>
        <Link to="/home" className="dw-nav-brand" style={{ textDecoration: 'none', color: 'inherit' }}>
          <DiwaSigil size={36} />
          DIWA
        </Link>
        <button className="dw-appbar-btn dw-appbar-btn--badged" aria-label="Open control plane" onClick={() => setRailOpen(true)}>
          ✦
          {needsYouCount > 0 && <span className="dw-appbar-dot">{needsYouCount}</span>}
        </button>
      </header>

      {(navOpen || railOpen) && (
        <div
          className="dw-scrim"
          onClick={() => {
            setNavOpen(false)
            setRailOpen(false)
          }}
        />
      )}

      <nav className={`dw-nav dw-panel${navOpen ? ' dw-nav--open' : ''}`}>
        {/* Pinned top — the wordmark never scrolls. */}
        <Link to="/home" className="dw-nav-brand dw-nav-brand--pinned" style={{ textDecoration: 'none', color: 'inherit' }}>
          <DiwaSigil size={40} />
          DIWA
        </Link>

        {/* Scrolling middle — the workspace spine. */}
        <div className="dw-nav-scroll">
          <div className="dw-nav-links">
            {/* One clean spine, ordered by the loop: land → look → tend → read → act → commune. */}
            <NavLink to="/home" end className={navClass}>Home</NavLink>
            <NavLink to="/graph" className={navClass}>Constellation</NavLink>
            <NavLink to="/domains" className={navClass}>Domains</NavLink>
            <NavLink to="/pipelines" className={navClass}>Springs</NavLink>
            <NavLink to="/signals" className={navClass}>Signals</NavLink>
            <NavLink to="/ledger" className={navClass}>Ledger</NavLink>
            <NavLink to="/missions" className={navClass}>Missions</NavLink>
            <NavLink to="/agents" className={navClass}>Agents</NavLink>
            <NavLink to="/commune" className={navClass}>Commune</NavLink>
          </div>
        </div>

        {/* Pinned bottom — collapsed "More" group + the by-diwata mark, neither scrolls. */}
        <div className="dw-nav-foot">
          <div
            className={`dw-nav-more${moreOpen ? ' dw-nav-more--open' : ''}`}
            onMouseEnter={() => setMoreHover(true)}
            onMouseLeave={() => setMoreHover(false)}
          >
            <button
              type="button"
              className="dw-nav-more-summary"
              aria-expanded={moreOpen}
              onClick={() => setMorePinned((v) => !v)}
            >
              <span>⚙ More</span>
              <span className="dw-nav-more-caret">{moreOpen ? '▾' : '▸'}</span>
            </button>
            <div className="dw-nav-more-body" hidden={!moreOpen}>
              <Feedback />
              <NavLink to="/profile" className={navClass}>Profile</NavLink>
              <NavLink to="/map" className={navClass}>How it connects</NavLink>
              <NavLink to="/sigils" className={navClass}>Sigils</NavLink>
              <NavLink to="/pricing" className={navClass}>Plan</NavLink>
              <NavLink to="/settings" className={navClass}>Settings</NavLink>
              {/* Appearance is reachable from anywhere: the full editor + the quick light/dark flip. */}
              <div className="dw-nav-appearance">
                <AppearancePanel align="left" />
                <ThemeToggle />
              </div>
            </div>
          </div>
          <a className="iv-nav-item dw-by-diwata" href="https://diwata.domains" target="_blank" rel="noreferrer">by Diwata ✦</a>
        </div>
      </nav>

      <main className="dw-main">
        {/* Route-aware "▸ Show me" guided tour — renders only where a tour exists. */}
        <ShowMe />
        <Outlet />
      </main>

      <ControlPlane open={railOpen} />

      {/* Desktop fold tabs: the sky can take the whole window when you want it to. */}
      <button
        type="button"
        className="dw-fold dw-fold--left"
        aria-label={navFolded ? 'Show navigation' : 'Hide navigation'}
        onClick={() => foldNav(!navFolded)}
      >
        {navFolded ? '⟩' : '⟨'}
      </button>
      <button
        type="button"
        className="dw-fold dw-fold--right"
        aria-label={railFolded ? 'Show control plane' : 'Hide control plane'}
        onClick={() => foldRail(!railFolded)}
      >
        {railFolded ? `⟨${needsYouCount > 0 ? ' ◈' : ''}` : '⟩'}
      </button>

      {/* One-thumb triage: the loop's postures, pinned to the bottom on phones. */}
      <nav className="dw-bottomnav dw-panel" aria-label="Triage">
        <NavLink to="/needs" className={bottomNavClass}>
          <span className="dw-bottomnav-glyph dw-bottomnav-glyph--badged">
            ◈{needsYouCount > 0 && <span className="dw-appbar-dot">{needsYouCount}</span>}
          </span>
          Needs you
        </NavLink>
        <NavLink to="/signals" className={bottomNavClass}>
          <span className="dw-bottomnav-glyph">●</span>
          Signals
        </NavLink>
        <NavLink to="/missions" className={bottomNavClass}>
          <span className="dw-bottomnav-glyph">◇</span>
          Missions
        </NavLink>
        <NavLink to="/graph" className={bottomNavClass}>
          <span className="dw-bottomnav-glyph">◎</span>
          Stars
        </NavLink>
        <NavLink to="/familiar" className={bottomNavClass}>
          <span className="dw-bottomnav-glyph">✦</span>
          Familiar
        </NavLink>
      </nav>
    </div>
    </EntityPeekProvider>
  )
}

function bottomNavClass({ isActive }: { isActive: boolean }) {
  return isActive ? 'dw-bottomnav-item dw-bottomnav-item--active' : 'dw-bottomnav-item'
}
